import Button from "../../components/ui/Button";
import Modal from "../../components/ui/Modal";
import { toast } from "react-toastify";
import Select from "../../components/ui/Select";
import React, { useState, useEffect } from "react";
import "../EditVendorModal/EditVendorModal.css";
import { ProductCategory } from "../../types/vendor";

interface AddProductModalProps {
  onClose: () => void;
  onSave: (formData: FormData) => void;
  isSaving: boolean;
}

const CATEGORIES: { value: ProductCategory; label: string }[] = [
  { value: "clothes", label: "Clothes" },
  { value: "shoes", label: "Shoes" },
  { value: "bags", label: "Bags" },
  { value: "household", label: "Household" },
  { value: "kids", label: "Kids" },
  { value: "baby", label: "Baby" },
  { value: "food", label: "Food" },
];

const AddProductModal: React.FC<AddProductModalProps> = ({
  onClose,
  onSave,
  isSaving,
}) => {
  const [fields, setFields] = useState({
    name: "",
    description: "",
    quantity: 1,
    category: "clothes" as ProductCategory,
    priceNGN: "",
    priceGBP: "",
  });

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Cleanup preview URL to avoid memory leaks
  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFields((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 1. Validation: Image
    if (!imageFile) {
      toast.warning("Please upload a product image");
      return;
    }

    // 2. Validation: Price
    // Ensure we have numbers to check
    const valNGN = Number(fields.priceNGN);
    const valGBP = Number(fields.priceGBP);

    if (valNGN <= 0 && valGBP <= 0) {
      toast.warning("Please enter a valid price greater than 0 for either NGN or GBP.");
      return;
    }

    const formData = new FormData();

    // 3. Basic Fields
    formData.append("name", fields.name);
    formData.append("description", fields.description);
    formData.append("quantity", String(fields.quantity));
    // API expects lowercase enum values (shoes, clothes, …)
    formData.append("category", fields.category.toLowerCase());

    if (valNGN > 0) formData.append("priceNGN", String(valNGN));
    if (valGBP > 0) formData.append("priceGBP", String(valGBP));

    formData.append("image", imageFile);

    onSave(formData);
  };

  return (
    <Modal title={<>Add New Product</>} onClose={onClose} size="medium">
<div className="modal-content legacy-modal-content">

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">
              Product Name <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={fields.name}
              onChange={handleChange}
              placeholder="e.g. Vintage T-Shirt"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={fields.description}
              onChange={handleChange}
              rows={3}
              required
            />
          </div>

          <div
            className="form-grid"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "20px",
            }}
          >
            <div className="form-group">
              <label htmlFor="category">Category</label>
              <Select
                id="category"
                name="category"
                value={fields.category}
                onChange={handleChange}
                required
              >
                {CATEGORIES.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </Select>
            </div>
            <div className="form-group">
              <label htmlFor="quantity">Quantity</label>
              <input
                type="number"
                id="quantity"
                name="quantity"
                value={fields.quantity}
                onChange={handleChange}
                min="1"
                required
              />
            </div>
          </div>

          {/* Pricing Section */}
          <div
            className="form-group"
            style={{
              marginTop: "10px",
              background: "#f9fafb",
              padding: "15px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
            }}
          >
            <label
              style={{
                marginBottom: "12px",
                display: "block",
                fontWeight: "600",
              }}
            >
              Pricing
            </label>
            <p
              style={{ fontSize: "12px", color: "#666", marginBottom: "10px" }}
            >
              Enter price in at least one currency.
            </p>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "20px",
              }}
            >
              <div className="form-group">
                <label style={{ fontSize: "13px", color: "#6B7280" }}>
                  Price (NGN)
                </label>
                <input
                  type="number"
                  placeholder="0.00"
                  name="priceNGN"
                  value={fields.priceNGN}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label style={{ fontSize: "13px", color: "#6B7280" }}>
                  Price (GBP)
                </label>
                <input
                  type="number"
                  placeholder="0.00"
                  name="priceGBP"
                  value={fields.priceGBP}
                  onChange={handleChange}
                  min="0"
                  step="0.01"
                />
              </div>
            </div>
          </div>

          {/* Image Section */}
          <div className="form-group">
            <label htmlFor="image">
              Product Image <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="file"
              id="image"
              name="image"
              accept="image/*"
              onChange={handleFileChange}
              required
            />

            {/* Image Preview Area */}
            {imagePreview && (
              <div
                style={{
                  marginTop: "10px",
                  width: "100px",
                  height: "100px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  overflow: "hidden",
                }}
              >
                <img
                  src={imagePreview}
                  alt="Preview"
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
            )}
          </div>

          <div
            className="modal-actions"
            style={{
              marginTop: "20px",
              display: "flex",
              gap: "10px",
              justifyContent: "flex-end",
            }}
          >
            <Button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button variant="primary" type="submit" className="btn-save" disabled={isSaving}>
              {isSaving ? "Uploading..." : "Add Product"}
            </Button>
          </div>
        </form>
      </div>
</Modal>
  );
};

export default AddProductModal;
